/** @file packShelves: packs rectangles onto one sheet in shelves (rows), tallest first. */
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { IMAGE_IO_ERRORS } from '../errors/imageIoErrors';
import { nextPowerOfTwo } from './nextPowerOfTwo';

/** One rectangle to place. */
export type PackInput = { id: string; w: number; h: number };

/**
 * `padding` (default 0) surrounds every rect on all sides; `maxWidth` defaults to
 * `nextPowerOfTwo(max(widest, ceil(sqrt(total area))))` over padded sizes; `powerOfTwo` rounds the sheet up.
 */
export type PackOptions = { padding?: number; maxWidth?: number; powerOfTwo?: boolean };

/** The sheet size, each rect's position (input order, padding excluded) and how many shelves were opened. */
export type PackResult = { width: number; height: number; rects: { id: string; x: number; y: number; w: number; h: number }[]; shelves: number };

/** The default width limit over padded sizes. */
function defaultMaxWidth(inputs: PackInput[], pad: number): number {
  const widest = Math.max(0, ...inputs.map((r) => r.w + pad * 2));
  const area = inputs.reduce((s, r) => s + (r.w + pad * 2) * (r.h + pad * 2), 0);
  return nextPowerOfTwo(Math.max(widest, Math.ceil(Math.sqrt(area))));
}

/**
 * Shelf packing: sort by height desc, width desc, id asc; each rect goes on the current shelf if it fits in the
 * remaining width, else a new shelf of its height starts below. A rect wider than `maxWidth` throws `PACK_TOO_WIDE`.
 */
export function packShelves(inputs: PackInput[], options: PackOptions = {}): PackResult {
  if (inputs.length === 0) return { width: 0, height: 0, rects: [], shelves: 0 };
  const pad = options.padding ?? 0;
  const maxWidth = options.maxWidth ?? defaultMaxWidth(inputs, pad);
  const order = inputs.map((_, i) => i).sort((i, j) => {
    const [a, b] = [inputs[i] as PackInput, inputs[j] as PackInput];
    return b.h - a.h || b.w - a.w || (a.id < b.id ? -1 : a.id > b.id ? 1 : i - j);
  });
  const rects = inputs.map((r) => ({ id: r.id, x: 0, y: 0, w: r.w, h: r.h }));
  let [x, y, shelfH, shelves, width] = [0, 0, 0, 0, 0];
  for (const i of order) {
    const r = rects[i] as PackResult['rects'][number];
    const [pw, ph] = [r.w + pad * 2, r.h + pad * 2];
    if (pw > maxWidth) throwError(IMAGE_IO_ERRORS.PACK_TOO_WIDE, { id: r.id, width: pw, maxWidth }, { hint: 'raise maxWidth or split the image' });
    if (shelves === 0 || x + pw > maxWidth) [x, y, shelfH, shelves] = [0, y + shelfH, ph, shelves + 1];
    [r.x, r.y] = [x + pad, y + pad];
    x += pw;
    width = Math.max(width, x);
  }
  const height = y + shelfH;
  return options.powerOfTwo === true
    ? { width: nextPowerOfTwo(width), height: nextPowerOfTwo(height), rects, shelves }
    : { width, height, rects, shelves };
}
